import {Component, inject, OnInit} from '@angular/core';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {ApiService} from '../../api.service';
import { UpperCasePipe, DecimalPipe, DatePipe } from '@angular/common';
import {Title} from '@angular/platform-browser';
import {PDFDocument} from 'pdf-lib';
import {ReturnsStore} from "../../core/store/returns.state";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {MatDialog} from "@angular/material/dialog";
import {ErrorNotificationModalComponent} from "../error-notification-modal/error-notification-modal.component";
import {take} from "rxjs";
import {HttpErrorResponse} from "@angular/common/http";
import {
	ClientModel,
	ItemFromYouReturnsResponseModel,
	YouReturnsResponseModel
} from "../../core/models/client-returns-data.model";
import {buildReturnFormPdfBytes} from "../../core/utils/return-form-pdf.util";
import {
	base64ToUint8Array,
	buildReturnFormLabels
} from "../../core/utils/return-document.util";
import {downloadPdf} from "../../core/utils/pdf-download.util";

@Component({
	selector: 'app-your-return',
	templateUrl: './your-return.component.html',
	styleUrls: ['./your-return.component.scss'],
	standalone: true,
	imports: [UpperCasePipe, DecimalPipe, TranslateModule, DatePipe]
})
export class YourReturnComponent implements OnInit {
	private _apiService = inject(ApiService);
	private _route = inject(ActivatedRoute);
	private _router = inject(Router);
	private _titleService = inject(Title);
	private _store = inject(ReturnsStore);
	private _dialogRef = inject(MatDialog);

	items: ItemFromYouReturnsResponseModel[] = [];
	client: ClientModel | null = null;
	productBrand = this._store.getCurrentState().brand;
	general_data: any = {};
	return_no: number | null = null;
	form = this._store.getCurrentState().hasForm;
	email: string = '';
	order_num: string = '';
	pdf = '';
	urlData = [];
	translateService = inject(TranslateService);

	ngOnInit(): void {
		this._enablePageLoader();
		this._setPageTitle();
		this._route.queryParams.subscribe((params) => {
			this._getQueryParams(params);
			this._makeInit(params);
		});
	}

	input_change(element: any) {
		this._updatePDFOnInputChangeOnFirstAndLastname(element);
		this._updatePDFOnInputChangeOnPostCode(element);
		this._onOtherInputChanges(element);
	}

	back() {
		let param_email = btoa(this.email);
		let param_order = btoa(this.order_num);
		this._router.navigateByUrl(
			`orders/${this._store.getCurrentState().brand}?lang=${this.translateService.currentLang}&email=${param_email}&order=${param_order}&formonly=${this.form}`
		).then();
	}

	async downloadLabels() {
		this._enablePageLoader();
		try {
			if (!this.client) {
				throw new Error('Missing client data');
			}
			const formPdfBytes = await buildReturnFormPdfBytes({
				brand: this.productBrand,
				returnNumber: this.general_data.return_no ?? this.return_no ?? '',
				orderNumber: this.general_data.order_number ?? this.order_num,
				returnDate: this.general_data.return_date ?? '',
				client: this.client,
				items: this.items.map((item) => ({
					article_code: item.article_code,
					quantity: item.quantity,
					return_reason: item.return_reason,
					return_reason_description: item.return_reason_description,
					damage_description: item.damage_description
				})),
				labels: buildReturnFormLabels(this.translateService.instant.bind(this.translateService))
			});
			const backendPdfBytes = base64ToUint8Array(this.pdf);
			const mergedPdf = await PDFDocument.create();
			for (const document of [formPdfBytes, backendPdfBytes]) {
				let pdfDoc1;
				try {
					pdfDoc1 = await PDFDocument.load(document);
				} catch (e) {
					this._dialogRef.open(ErrorNotificationModalComponent, {
						data: {
							title: this.translateService.instant('returnsViewPage.error'),
							message: this.translateService.instant('returnsViewPage.labelGenerateError')
						}
					})
						.afterClosed()
						.pipe(take(1))
						.subscribe();
					return;
				}

				const copiedPages = await mergedPdf.copyPages(pdfDoc1, pdfDoc1.getPageIndices());
				copiedPages.forEach((page) => mergedPdf.addPage(page));
			}

			const pdfBytes = await mergedPdf.save();
			downloadPdf(pdfBytes, this._apiService.generateDownloadFileName(this.order_num));
		} finally {
			this._dismissPageLoader();
		}
	}

	private _setPageTitle() {
		this._titleService.setTitle('AstorMueller | Your order');
	}

	private _getQueryParams(params: Params) {
		this.email = decodeURIComponent(atob(params.email));
		this.order_num = decodeURIComponent(atob(params.order));
		this.return_no = params.return_no ? Number(params.return_no) : null;
		this.urlData = params?.data ? JSON.parse(decodeURIComponent(atob(params.data))) : null;
	}

	private _isAlreadyReturned() {
		return this.return_no != null;
	}

	private _showPageWithAlreadyMadeReturn() {
		this._showYourReturnDocumentContent();
		this._deactivateInputs();
	}

	private _addReturnNoToPDF() {
		if (!this.general_data?.return_no) {
			return;
		}
		this.general_data.return_no = (this.return_no as number);
	}

	private _deactivateInputs() {
		let allInputs = (document.querySelectorAll('input'));
		for (let i = 0; i < allInputs.length; i++) {
			allInputs[i].disabled = true;
		}
	}

	private _showYourReturnDocumentContent() {
		this._apiService.get_your_return_document(this.return_no!).subscribe(
			(data) => {
				this._setGlobalVariablesValue(data);
				// this._makeCalculations(data.positions, data.positions);
				this._dismissPageLoader();
				this.pdf = data.label;
				this._addReturnNoToPDF();
			},
			(error: HttpErrorResponse) => {
				this._onInitError(error);
			}
		);
	}

	private _makeInit(params: Params) {
		if (this._isAlreadyReturned()) {
			this._showPageWithAlreadyMadeReturn();
			return;
		}
		const data = params.data ? decodeURIComponent(atob(params.data)) : '';
		const json_data = data ? JSON.parse(data) : [];
		let items = '';
		for (let i = 0; i < json_data.length; i++) {
			items = items + `${json_data[i].sku},`;
		}

		this._apiService.your_return(this.email, this.order_num).subscribe(
			(data) => {
				this._redirectIfJsonResponseIsNotValid(data);
				// @ts-ignore
				this._setGlobalVariablesValue(data);
				// this._makeCalculations(json_data, data);
				// this._setTransportCostIfNeeded();
				this._dismissPageLoader();
			},
			(error: HttpErrorResponse) => {
				this._onInitError(error);
			}
		);
	}

	private _dismissPageLoader() {
		(document.getElementById('page_loader') as HTMLElement).classList.add('no-display');
	}

	private _enablePageLoader() {
		(document.getElementById('page_loader') as HTMLElement).classList.remove('no-display');
	}

	private _setGlobalVariablesValue(JSON: YouReturnsResponseModel) {
		this.items = JSON.positions;
		this.general_data = JSON;
		this.client = JSON.client;
	}

	private _redirectIfJsonResponseIsNotValid(JSON: any) {
		if (JSON.length < 1 || JSON.positions.length < 1) {
			this._router.navigateByUrl(`?lang=${this.translateService.currentLang}&formonly=${this.form}`).then();
		}
	}

	private _onInitError(error: HttpErrorResponse) {
		if (error.status === 503) {
			this._dismissPageLoader();
			return;
		}
		this._dialogRef.open(ErrorNotificationModalComponent, {
			data: {
				title: this.translateService.instant('returnsViewPage.error'),
				message: error?.error?.message ? error?.error?.message : this.translateService.instant('returnsViewPage.labelGenerateError')
			}
		})
		this._dismissPageLoader();
		if (error.error && error.error?.message && error?.error?.message.indexOf('DHL') > -1) {
			return;
		}
		this._router.navigateByUrl(
			`?lang=${this.translateService.currentLang}&formonly=${this.form}`
		).then();
	}

	private _updatePDFOnInputChangeOnFirstAndLastname(element: any) {
		if (element.target.dataset.name == 'first_nd_lastname') {
			let full_name = element.target.value.trim().split(' ');
			let first_name = '';
			let last_name = '';
			for (let i = 0; i < full_name.length; i++) {
				if (i < full_name.length - 1) {
					first_name += ' ' + full_name[i];
				} else {
					last_name = full_name[i];
				}
			}
			if (first_name && this.client?.firstname) {
				this.client.firstname = first_name;
			}
			if (last_name && this.client?.lastname) {
				this.client.lastname = last_name;
			}
		}
	}

	private _onOtherInputChanges(element: any) {
		// @ts-ignore
		this.client[element.target.dataset.name] = element.target.value;
	}

	private _updatePDFOnInputChangeOnPostCode(element: any) {
		if (element.target.dataset.name == 'postcode') {
			const country_code = ((document.getElementById('country') as HTMLInputElement).value);

			if (country_code.toUpperCase() == 'AT' && element.target.value.length <= 4) {
				// @ts-ignore
				this.client[element.target.dataset.name] = element.target.value;
				// @ts-ignore
				this.general_data = this.client;
			} else if (country_code.toUpperCase() == 'AT') {
				((document.getElementById('postcode') as HTMLInputElement).value) = element.target.value.substring(0, 4);
			}
		}
	}
}
